import { Component, ViewEncapsulation, OnInit, Output, EventEmitter, Input, Inject, LOCALE_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { Mote, SimpleTranslationMap, GeoTourTag, TourTag } from '../../models';
import { TourTagLabel, FancyTourTagLabel } from '../../models/tour-tag-label'
import { MotesLoginService, UserRights } from '../../motes-login';
import { AutoSub, AutoSubOnDestroy, LanguageService, MoteService, TourMap, ToursFilterService } from '../../providers';
import { HorizontalScrollService } from '../horizontal-scroll/horizontal-scroll.service';
import { CitySearchResult, CitySearchResults } from '../../models/db-types';
import { SupabaseService } from '../../providers/supabase.service';
import { MotesCookieService } from '../../cookie/cookie.service';
import { Animations } from '../../animations';

export type ViewMode = 'categories' | 'search';

@AutoSubOnDestroy()
@Component({
    selector: 'tour-categories',
    templateUrl: './tour-categories.component.html',
    styleUrls: ['./tour-categories.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.popIn(100)]
})
export class TourCategoriesComponent implements OnInit {

    @Output() openTour = new EventEmitter<Mote>();
    @Output() viewModeChange = new EventEmitter<ViewMode>();
    @Input() editModeEnabled: boolean = false;
    // The tour route to be used when clicking a tour, ie. '/tours/' (tourKey will be added at the end).
    @Input() tourRoute: string = '/tours/';

    public tourMap: TourMap = {};
    public shownTours: Mote[] = [];
    public shownCities: CitySearchResults = [];

    public translationMap: SimpleTranslationMap = {};
    public categories: TourTag[] = [];
    public viewMode: ViewMode = 'categories';
    public resultMessage: string;
    public userRights: UserRights;
    public loading: boolean = true;
    public loadingTourKey: string = "";
    public hideCities: boolean = false;

    public selectedCity: CitySearchResult = null;
    public showRequestTour: boolean = false;
    public showRequestEmail: boolean = false;
    public isTourRequested: boolean = false;
    public tourRequestEmail: string = "";

    // Expose types to view
    public TourTagLabel = TourTagLabel;
    public FancyTourTagLabel = FancyTourTagLabel;
    public GeoTourTag = GeoTourTag;

    constructor(
        public toursFilterService: ToursFilterService,
        private moteService: MoteService,
        private loginService: MotesLoginService,
        private scrollService: HorizontalScrollService,
        private db: SupabaseService,
        private cookieService: MotesCookieService,
        private router: Router,
        @Inject(LOCALE_ID) private locale: string,
    ) { }

    ngOnInit() {
        this.categories = Object.values(GeoTourTag);

        AutoSub(this).reg['allToursSub'] = this.toursFilterService.watchAllTours().pipe(map((allTours) => {
            return LanguageService.getSimpleTranslationMap(allTours);
        })).subscribe((translationMap: SimpleTranslationMap) => {
            this.translationMap = translationMap;
        })

        AutoSub(this).reg['shownToursSub'] = this.toursFilterService.watchSearchResults().subscribe((results) => {
            const { tourResults, cityResults } = results; 
            this.updateView(tourResults, cityResults);
        })

        AutoSub(this).reg['userSub'] = this.loginService.watchUserRights().subscribe(userRights => {
            this.userRights = userRights;
        });

        AutoSub(this).reg['routeSub'] = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            // Reset loading tour key when routing away from page
            this.loadingTourKey = "";
        });
    }

    private updateView(shownTours: Mote[], shownCities?: CitySearchResults) {
        this.loading = true;
        this.showRequestTour = false;
        this.isTourRequested = false;
        this.showRequestEmail = false;

        this.viewMode = this.toursFilterService.searchInput.length > 0 ? 'search' : 'categories';
        this.viewModeChange.emit(this.viewMode);

        switch(this.viewMode) {
            case 'search':
                this.scrollToTop();
                this.shownTours = shownTours;
                this.shownCities = shownCities;
                this.resultMessage = $localize`Tours (${this.shownTours.length})`;
                this.loading = false;
                this.hideCities = false;
                break;
            case 'categories':
                this.tourMap = this.toursFilterService.categorizeTours(shownTours, this.categories)
                const toursCount = Object.values(this.tourMap).reduce((sum, list) => sum + list.length, 0);
                this.resultMessage = toursCount == 0 ? $localize`We couldn't find any tours matching your filters.` : null;
                // this.scrollService.forceScrollsUpdate();
                this.loading = false;
            break;
        }
    }

    private scrollToTop() {
        if(!window) return;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    public isTourMapEmpty(tourMap: TourMap): boolean {
        return Object.values(tourMap).every((tourList) => tourList.length <= 0);
    }

    public saveTourEdit(tour) {
        this.moteService.saveMoteTags([tour]);
        this.moteService.saveMoteOrder([tour]);
    }

    public tourClicked(tour: Mote) {
        if(this.editModeEnabled) return;
        if(this.tourRoute) this.loadingTourKey = tour?.key || '';
        this.openTour.emit(tour);
    }

    public cityClicked(city: CitySearchResult) {
        this.shownCities = [];
        this.selectedCity = city;
        const allToursInCity = this.toursFilterService.allTours.filter(tour => tour.worldCityId == city.id);
        this.shownTours = LanguageService.filterMotesByLocale(allToursInCity, this.locale);
        if(this.shownTours.length <= 0) {
            this.resultMessage = $localize`Sorry, no StoryHunt in ${city.name} - yet!`;
            this.showRequestTour = true;
        }
        else {
            this.resultMessage = $localize`Tours in ${city.name} (${this.shownTours.length})`;
        }

        this.hideCities = true;
        this.toursFilterService.searchInput = `${city.name}, ${city.country}`;
    }

    public trackByTourKey(index: number, tour: Mote) {
        return tour.key;
    }

    public requestTour() {
        this.showRequestEmail = true;
    }

    public async saveTourRequest() {
        this.showRequestEmail = false;
        this.showRequestTour = false;
        this.isTourRequested = true;
        this.resultMessage = $localize`Thank you for the request, we will let you know.`;
        const visitorKey = await this.cookieService.getVisitorKey();
       
        const cli = this.db.getAnonSupabaseClient();

        const { data, error } = await cli.from('tour_requests').insert({
            email: this.tourRequestEmail,
            visitorKey: visitorKey,
            cityName: this.selectedCity?.name,
            worldCityId: this.selectedCity?.id,
        });
        if(error) console.warn("Tour request failed..", error);
    }
}
